import React from 'react';
import { REPORTS } from '../pages/admin/ReportsPage';
import { moduleOn } from './modules';

/*
  ONE menu, for every job level and every screen width.

  Built 5 September 2026 for "One shell, one menu, three homes", against the
  approved shell in docs/design/shell/ and the approved menu in
  docs/design/group-c/. Before this file there were SIX menus, and they had
  drifted apart in ways nobody could see from any one screen:

    AdminDashboard    a two-tier menu of areas and pages, and a separate
                      three-item dock below 1024 (Cycle, Settings, Sign Out)
    ManagerDashboard  a flat one-tier menu, and a separate four-item dock
    RepDashboard      a flat one-tier menu, and a separate four-item dock

  The rep's two disagreed with each other about what things are called. On a
  desk: My Counts, Tasks, Reports, History. On a phone: Locations, History,
  Tasks & Alerts, Sign Out. Same person, same app, and "My Counts" and
  "Locations" are the same screen.

  The owner's reason for this job, in his own words: if a design change has to
  be made in three or more places, it will be made in three or more places every
  time, forever.

  WHAT THIS FILE DOES NOT DO. It names only pages that exist today. A menu entry
  for a page nobody has built is a door onto nothing, which is the same fault as
  a page nobody can reach seen from the other side. **Home joined it on 5
  September 2026, the day it was built, the manager's History and My Exports
  joined it later the same day, and the Review Hub joined it on 5 September when
  it stopped being a dialog and became a page.** Every page this file names
  exists.
*/

// -- The marks ----------------------------------------------------------------
// Kept together rather than inline beside each page, because the same mark is
// used by the two-tier menu on a desk and by the dock below 1024, and two
// copies of one drawing is how the six menus drifted in the first place.
export const ICON = {
  home:       <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></>,
  counts:     <><rect x="5" y="4" width="14" height="17" rx="2" /><path d="M9 4V2h6v2" /><path d="m9 13 2 2 4-4" /></>,
  warehouse:  <><path d="M3 21V8l9-5 9 5v13" /><path d="M7 21v-8h10v8" /><path d="M3 21h18" /></>,
  catalog:    <><path d="M12 2H2v10l9.3 9.3a1.7 1.7 0 0 0 2.4 0l7.6-7.6a1.7 1.7 0 0 0 0-2.4z" /><circle cx="7" cy="7" r="1.5" /></>,
  shrink:     <><path d="M3 3v18h18" /><path d="m19 15-5-5-4 4-3-3" /></>,
  ordering:   <><path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v6h-6" /></>,
  cart:       <><circle cx="9" cy="20" r="1.5" /><circle cx="17" cy="20" r="1.5" /><path d="M3 4h2l2.5 12h10L20 8H6" /></>,
  swap:       <><path d="M5 9l-3 3 3 3" /><path d="M2 12h13" /><path d="M19 15l3-3-3-3" /><path d="M22 12H9" /></>,
  reports:    <><path d="M3 3v18h18" /><rect x="7" y="10" width="3" height="7" /><rect x="13" y="6" width="3" height="11" /></>,
  people:     <><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0" /><path d="M16 4a3.5 3.5 0 0 1 0 7" /><path d="M21.5 20a6.5 6.5 0 0 0-4.5-6" /></>,
  settings:   <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></>,
  sites:      <><rect x="3" y="9" width="7" height="12" rx="1" /><rect x="14" y="4" width="7" height="17" rx="1" /><path d="M2 21h20" /></>,
  uploads:    <><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" /></>,
  person:     <><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="1" /><polyline points="9 12 11 14 15 10" /></>,
  clock:      <><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15.5 14" /></>,
  // A circle half light and half dark: the theme and light or dark mode.
  appearance: <><circle cx="12" cy="12" r="9" /><path d="M12 3a9 9 0 0 0 0 18z" fill="currentColor" /></>,
};

/*
  **Appearance is in every job level's Settings**, theme system phase 5, 14
  September 2026, by the implementation brief: every signed-in person gets a
  Settings area, reps and buyers included, and Appearance is visible to all.
  It is last in Settings for an admin and a manager, so the Settings tab still
  opens on the page it always did; for a rep and a buyer it is the whole area,
  so no page row is drawn for it. Never in the menu under a person's name.
*/
const APPEARANCE = { key: 'appearance', label: 'Appearance', icon: ICON.appearance };

// -- The four admin reports ---------------------------------------------------
// The report types ARE the page tier, so picking a report is a menu choice
// rather than an in-page sidebar. Group F settled that on 4 September 2026.
//
// **Imported rather than restated**, which is this file's whole point applied to
// itself: the reports screen already owns this list, and a second copy here
// would drift from it exactly the way the six menus drifted from each other.
const REPORT_PAGES = REPORTS.map(r => ({ key: r.key, label: r.label, icon: r.icon }));

// -- The menus ----------------------------------------------------------------
// **Home is the first item at every size**, which is rule 31's other half and
// Group C's decision of 31 August 2026: it is the screen a person lands on, and
// below 1024 there has to be a way back to it. An area holding one page draws no
// page row, so Home is one tab and no second tier.
const HOME = { key: 'home', label: 'Home', pages: [
  { key: 'home', label: 'Home', icon: ICON.home },
] };

const ADMIN = [
  HOME,
  /*
    **INVENTORY COUNTS IS A TOP-LEVEL AREA**, the owner's word of 8 October
    2026: *"I want inventory counts to become a tier 1 menu with the current
    inventory counts page as the landing page/dashboard for inventory counts.
    The sub menus for inventory counts would be My counts, Review hub and
    shrink analysis."* Warehouses and Item Catalog moved to Settings the same
    day, which left nothing else in what had been Operations.

    **The key stays `operations` on purpose.** It is what the site's always-on
    rule and every remembered menu position name, so renaming it would quietly
    switch the area off for nobody and lose people's place. Only the words a
    person reads changed. A manager's and a rep's Operations are untouched.
  */
  /* **"Counts" on a phone's tab bar**, which has about 78 pixels a tab at 375
     wide; "Inventory Counts" needs 91 and was cut off, measured 8 October. */
  { key: 'operations', label: 'Inventory Counts', tabLabel: 'Counts', pages: [
    { key: 'overview',            label: 'Inventory Counts',           icon: ICON.counts },
    // **My Counts is one screen for all three job levels**, which is rule 23's
    // own words: it exists for everybody and means the same thing. The admin
    // was the one who could not reach it, and an admin holds locations of their
    // own as readily as a manager does. Added 5 September 2026.
    { key: 'mycounts',            label: 'My Counts',                  icon: ICON.counts },
    // **The Review Hub is a page now**, 5 September 2026, by the owner's
    // decision of 31 August. It was a dialog with five tabs opened from a
    // button, and this file said it was "the only one still drawn and not
    // built". It is built.
    { key: 'review_hub',          label: 'Review Hub',                 icon: ICON.person },
    { key: 'shrink_analysis',     label: 'Shrink Analysis',            icon: ICON.shrink, soon: true },
  ] },
  /*
    **Job Queue is first, because it is where the work actually starts**, the
    owner's description of 3 October 2026: everything that arrives by any of the
    five doors is turned into one shape and waits in a queue for him to pick up.
    The screens after it are what he does with a job once he has it.
    **Replenishment was removed on 12 October 2026**, his word: *"we can remove
    the replenishment sub menu it is no longer needed since processing of an
    order will happen in the job queue."*

    **It is a mock-up today and says so on the screen**, in the pattern Shrink
    Analysis uses: in the menu before it is built, saying plainly what it is
    rather than promising anything.
  */
  { key: 'ordering_area', label: 'Orders', pages: [
    { key: 'job_queue',           label: 'Job Queue',                  icon: ICON.cart },
    { key: 'place_order',         label: 'Place an Order',             icon: ICON.cart },
    { key: 'client_ordering',     label: 'Client Ordering',            icon: ICON.cart },
    { key: 'inventory_movement',  label: 'Regional Product Movement',  icon: ICON.swap },
  ] },
  { key: 'reports', label: 'Reports', pages: REPORT_PAGES },
  { key: 'settings_area', label: 'Settings', pages: [
    { key: 'users_access',        label: 'Users & Access',             icon: ICON.people },
    { key: 'site_settings',       label: 'Site Settings',              icon: ICON.settings },
    // Moved here from Operations on 8 October 2026, the owner's word.
    { key: 'warehouses',          label: 'Warehouses',                 icon: ICON.warehouse },
    { key: 'catalog',             label: 'Item Catalog',               icon: ICON.catalog },
    /*
      **Order & Routing Rules**, asked for on 3 October 2026 with the two mapping
      pages, in the menu before it is built, which is the pattern Shrink
      Analysis already uses. **Admin only.** The two mapping pages moved to
      Setup, below, on 6 October 2026.
    */
    { key: 'order_rules',         label: 'Order & Routing Rules',      icon: ICON.ordering, soon: true },
    { key: 'sites',               label: 'Sites',                      icon: ICON.sites, globalOnly: true },
    { key: 'uploads',             label: 'Branding & Uploads',         icon: ICON.uploads },
    APPEARANCE,
  ] },
  /*
    **SETUP, 6 October 2026, the owner's word**: the mapping tools are for
    setting OnHand up the first time, and then they go. *"I truly just want the
    mappings for the initial system setup and then I want them gone."* After
    that, a change is made on the item or the place itself, or by a file import.

    **His alone**, so every page is `globalOnly` and the area drops for anybody
    who is not a Global Admin. **And it switches off**: it is a module, so
    Site Settings -> Modules -> Setup -> Hidden takes the whole area away.
    Nothing it saved is touched by hiding it.
  */
  { key: 'setup_area', label: 'Setup', pages: [
    { key: 'item_mapping',   label: 'Item Mapping',   icon: ICON.catalog, globalOnly: true },
    { key: 'vendor_mapping', label: 'Vendor Mapping', icon: ICON.swap,    globalOnly: true },
    { key: 'place_mapping',  label: 'Place Mapping',  icon: ICON.sites,   globalOnly: true },
  ] },
];

const MANAGER = [
  HOME,
  { key: 'operations', label: 'Operations', pages: [
    { key: 'overview',  label: 'Cycle Details', icon: ICON.counts },
    { key: 'mycounts',  label: 'My Counts',     icon: ICON.counts },
    { key: 'tasks',     label: 'Tasks',         icon: ICON.person },
    // **History joined the manager's menu on 5 September 2026**, the day it was
    // built. Group C's drawings have had it in this row since 31 August, and
    // this file's rule is that a page appears here on the day the page exists
    // and not before: a menu entry for a page nobody has built is a door onto
    // nothing.
    { key: 'history',   label: 'History',       icon: ICON.clock },
  ] },
  /*
    **My Exports joined the manager's menu on 5 September 2026**, the day it was
    built, and it is the LAST entry this file was withholding. Group C's drawings
    have had it since 31 August.

    With it, the rule this file opens with has nothing left outstanding: it names
    only pages that exist today, and every page in Group C's drawings now exists.
  */
  { key: 'exports', label: 'Reports', pages: [
    { key: 'reports',   label: 'My Exports',    icon: ICON.reports },
  ] },
  /*
    **A manager gets Users & Access, 8 September 2026.** The owner's decision of
    22 August was that a manager assigns people within their own region, and
    until now nothing in OnHand let them: the screen was administrator-only and
    the database refused anybody else outright.

    It is the same screen, showing what a manager may actually change. The
    people are the ones in their regions plus anybody covering nothing yet; the
    locations are the ones in their regions; the name, the email, the job level,
    the Active switch, Global Admin, creating a login and resetting a password
    are not theirs and are not offered. Every one of those is refused by the
    database as well.
  */
  { key: 'settings_area', label: 'Settings', pages: [
    { key: 'users_access', label: 'Users & Access', icon: ICON.people },
    APPEARANCE,
  ] },
];

// **The rep's menu is the one that was saying two different things.** On a desk
// it said My Counts, Tasks, Reports, History; below 1024 the dock said
// Locations, History, Tasks & Alerts, Sign Out. My Counts and Locations are the
// same screen. Both names are settled here, from docs/design/VOCABULARY.md:
// My Counts, and My Exports rather than Reports, which collided with the four
// admin reports and was renamed by Group C on 31 August 2026.
const REP = [
  HOME,
  { key: 'operations', label: 'Operations', pages: [
    { key: 'mycounts',  label: 'My Counts', icon: ICON.counts },
    { key: 'tasks',     label: 'Tasks',     icon: ICON.person },
    { key: 'history',   label: 'History',   icon: ICON.clock },
  ] },
  { key: 'exports', label: 'Reports', pages: [
    { key: 'reports',   label: 'My Exports', icon: ICON.reports },
  ] },
  { key: 'settings_area', label: 'Settings', pages: [APPEARANCE] },
];

/*
  A buyer.

  Added 5 September 2026, on the owner's decision. **`buyer` had been a real job
  level in the database since 21 August and nobody had ever said what one is
  for**, so this list had no entry for it and a buyer fell through to the rep's
  menu and the rep's screens: somebody whose job is ordering stock would have
  signed in to a screen asking them to count it. Nobody holds the job today, so
  it was latent rather than live, and silent, which is the shape of fault this
  project keeps finding.

  Put to him in plain words, with the alternative of removing the job level
  altogether. **A buyer is somebody who orders stock**, so they get the three
  Ordering screens and none of the counting ones.
*/
const BUYER = [
  HOME,
  /*
    **Job Queue is first, because it is where the work actually starts**, the
    owner's description of 3 October 2026: everything that arrives by any of the
    five doors is turned into one shape and waits in a queue for him to pick up.
    The screens after it are what he does with a job once he has it.
    **Replenishment was removed on 12 October 2026**, his word: *"we can remove
    the replenishment sub menu it is no longer needed since processing of an
    order will happen in the job queue."*

    **It is a mock-up today and says so on the screen**, in the pattern Shrink
    Analysis uses: in the menu before it is built, saying plainly what it is
    rather than promising anything.
  */
  { key: 'ordering_area', label: 'Orders', pages: [
    { key: 'job_queue',           label: 'Job Queue',                  icon: ICON.cart },
    { key: 'place_order',         label: 'Place an Order',             icon: ICON.cart },
    { key: 'client_ordering',     label: 'Client Ordering',            icon: ICON.cart },
    { key: 'inventory_movement',  label: 'Regional Product Movement',  icon: ICON.swap },
  ] },
  { key: 'settings_area', label: 'Settings', pages: [APPEARANCE] },
];

const BY_ROLE = { admin: ADMIN, manager: MANAGER, rep: REP, buyer: BUYER };

/*
  The menu one person may actually use.

  Two filters, both the app's own existing rules, moved here so they stop being
  the admin screen's private business: a module switched off in Site Settings
  takes its pages out of the menu entirely rather than hiding them with styling,
  and `globalOnly` pages are dropped for anybody who is not a global admin. An
  area left with no pages drops with them. That is the shape the approved shell already expects: it draws no
  page row for an area holding one page, and no area tab for an area holding
  none.
*/
export function menuFor(role, { isGlobalAdmin = false, modules = null } = {}) {
  const areas = BY_ROLE[role] || REP;
  return areas
    .filter(a => moduleOn(modules, a.key))
    .map(a => ({
      ...a,
      pages: a.pages.filter(p => moduleOn(modules, p.key)
        && (!p.globalOnly || isGlobalAdmin)),
    }))
    .filter(a => a.pages.length > 0);
}

// Every page one person may reach, flattened. Used to validate a saved tab and
// to look a page's name up for the bar.
export function pagesFor(role, opts) {
  return menuFor(role, opts).flatMap(a => a.pages);
}

// Which area a page belongs to, so the dock can light the right one.
export function areaOf(role, pageKey, opts) {
  const areas = menuFor(role, opts);
  return areas.find(a => a.pages.some(p => p.key === pageKey)) || areas[0];
}

// A page by key, for its label and its mark.
export function pageOf(role, pageKey, opts) {
  return pagesFor(role, opts).find(p => p.key === pageKey) || null;
}
